// The popup that says what the cleanup was worth.
//
// Owner, 2026-08-24, on the "Nettoyer maintenant" button: "add here pop up
// infos notif how many GB we did saved at the end after clicking clean button
// and some animation".
//
// ⚠️ THE NUMBER IS THE POINT, so it gets the whole middle of the card and the
// animation exists to draw the eye to it — a count-up from zero, which is the
// one motion that makes a figure feel measured rather than declared. Everything
// else (the ring, the fade) is there to stop the card appearing abruptly, not
// to be noticed.
//
// ⚠️ IT NEVER APPEARS FOR A NO-OP. `didSomething` gates it: most runs of a
// retention cleanup delete nothing, and a popup cheering "0 KB freed" teaches
// the reader to dismiss the one that matters.

import { useEffect, useRef, useState } from 'react';
import { formatFreed, freedDetail, remainingNote, pdfSkipNote, type CleanupOutcome } from './cleanupResult';

const GOLD = '#C9A24A';

/**
 * Count from 0 to `to` over `ms`, easing out.
 *
 * ⚠️ rAF, NOT setInterval, and it respects `prefers-reduced-motion` by landing
 * on the final value immediately — a number spinning up the screen is exactly
 * the kind of motion that setting exists to turn off.
 */
function useCountUp(to: number, ms = 900): number {
  const [n, setN] = useState(0);
  const raf = useRef<number>(0);
  useEffect(() => {
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !(to > 0)) { setN(to); return undefined; }
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      // easeOutCubic — fast first, settles gently on the real figure.
      setN(to * (1 - (1 - p) ** 3));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [to, ms]);
  return n;
}

export function CleanupResultModal({ result, onClose }: {
  result: CleanupOutcome;
  onClose: () => void;
}) {
  const freed = formatFreed(result.bytes_freed);
  const shown = useCountUp(freed.value);
  // Below ten the figure carries one decimal, so the count-up has to as well or
  // it jumps from "2" to "2,4" at the very end.
  const text = (freed.value >= 10 ? Math.round(shown) : Math.round(shown * 10) / 10)
    .toString().replace('.', ',');

  // Escape closes it, like every other dialog on this admin.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      style={{ animation: 'storiz-fade-in 180ms ease-out' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Résultat du nettoyage"
    >
      <div
        className="relative w-full max-w-sm rounded-2xl bg-white px-6 py-7 text-center shadow-2xl"
        style={{ animation: 'storiz-pop-in 260ms cubic-bezier(.16,1,.3,1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* The ring sweeps once behind the icon — motion that says "finished",
            not "working". A spinner here would read as still running. */}
        <div className="mx-auto mb-4 relative h-16 w-16">
          <svg viewBox="0 0 64 64" className="absolute inset-0 h-16 w-16 -rotate-90">
            <circle cx="32" cy="32" r="28" fill="none" stroke="#F1EADA" strokeWidth="5" />
            <circle
              cx="32" cy="32" r="28" fill="none" stroke={GOLD} strokeWidth="5"
              strokeLinecap="round" strokeDasharray="176"
              style={{ animation: 'storiz-ring 900ms cubic-bezier(.16,1,.3,1) forwards' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-2xl">🗑️</div>
        </div>

        <p className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">
          Espace libéré
        </p>
        <p className="mt-1 text-[44px] font-bold leading-none text-gray-900 tabular-nums">
          {text}
          <span className="ml-1 text-[20px] font-semibold text-gray-500">{freed.unit}</span>
        </p>
        <p className="mt-3 text-[13px] text-gray-600">{freedDetail(result)}</p>
        {/* ⚠️ THE POPUP CELEBRATES A NUMBER, SO IT OWES THE CAVEATS TOO. A card
            that shows "2,4 GB libérés" and hides "5 333 restantes" is a nicer
            version of the same omission. */}
        {remainingNote(result) && (
          <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-800">
            {remainingNote(result)}
          </p>
        )}
        {pdfSkipNote(result) && (
          <p className="mt-2 text-[11.5px] text-gray-500">{pdfSkipNote(result)}</p>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-5 h-11 w-full rounded-xl text-[14px] font-semibold text-white"
          style={{ background: GOLD }}
          autoFocus
        >
          Parfait
        </button>
      </div>

      <style>{`
        @keyframes storiz-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes storiz-pop-in {
          from { opacity: 0; transform: translateY(10px) scale(.96) }
          to   { opacity: 1; transform: none }
        }
        @keyframes storiz-ring { from { stroke-dashoffset: 176 } to { stroke-dashoffset: 0 } }
        @media (prefers-reduced-motion: reduce) {
          [role="dialog"], [role="dialog"] > div { animation: none !important }
          [role="dialog"] circle { animation: none !important; stroke-dashoffset: 0 !important }
        }
      `}</style>
    </div>
  );
}
